/**
 * useCartSummary — single source of truth for the header badge AND
 * the mobile sticky bottom cart bar. Backed by `GET /api/cart/summary`.
 *
 * Refreshes:
 *   - on mount
 *   - every 30s
 *   - on window focus
 *   - whenever the global `cart:changed` event fires (dispatch this from
 *     anywhere you mutate the cart to get an instant update)
 */
import { useCallback, useEffect, useState } from 'react';
import { cartAPI, CartSummary } from '../api/cart';
import { useAuthStore } from '../stores/authStore';

const EMPTY: CartSummary = { item_count: 0, total: 0, store_count: 0, stores: [] };

export function useCartSummary(): CartSummary & { refresh: () => void } {
  const { isAuthenticated, user } = useAuthStore();
  const [summary, setSummary] = useState<CartSummary>(EMPTY);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'customer') {
      setSummary(EMPTY);
      return;
    }
    try {
      const s = await cartAPI.getSummary();
      setSummary(s ?? EMPTY);
    } catch {
      /* leave previous value; network blips shouldn't blank the UI */
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    const onFocus = () => refresh();
    const onCartChanged = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('cart:changed', onCartChanged);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('cart:changed', onCartChanged);
    };
  }, [refresh]);

  return { ...summary, refresh };
}

export default useCartSummary;

